/**
 * Sincronización masiva de catálogo desde Supabase (RPC get_mobile_catalog_snapshot).
 *
 * Módulo profundo: un solo punto de entrada público `syncCatalogSnapshotFromRemote`.
 * Internamente: cooldown/coalescing, reconcile diff, batch upserts, stock writes,
 * web cache adapter. Tipos remotos en `domain/catalogSnapshotTypes.ts`.
 */
import { dedupeDuplicateStocksRows, MIN_SUPPORTED_CATALOG_SNAPSHOT_SCHEMA_VERSION } from '../../shared/localDb'
import type { SqliteAsyncConnection } from '../../shared/localDb'
import { recordStartupNetworkCall } from '../../shared/dev/networkCallAudit'
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { supabase } from '../../shared/supabase/supabaseClient';
import { publishCatalogSynced } from '../../sync/core/catalogSyncListeners'
import { SyncPullFailedError } from '../../sync/core/syncPullErrors';
import { getCatalogLastPullAt, readCatalogMetaCounts } from '../../sync/core/pullDownHelpers'
import { persistAppSettingSafe } from '../../sync/core/persistAppSetting'
import { recordSyncPullTimestamp } from '../../sync/core/syncTimestamps'
import { withSyncMutex } from '../../sync/core/syncMutex';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { mergeRemoteSnapshotStocks, type RemoteSnapshotStockLotRow, type RemoteSnapshotStockRow, type RemoteSnapshotStockTransferRow } from './catalogSnapshotStockApply';
import { applyRemoteCatalogSnapshotStockWrites } from './catalogSnapshotStockApply';
import { markCatalogHydrated } from './catalogHydrationState';
import { getCatalogRamSnapshotCache, mergeCatalogRamSnapshotCache, setCatalogRamSnapshotCache } from './catalogRamSnapshotCache';
import { catalogSnapshotVerboseLog, catalogSnapshotVerboseWarn } from './catalogSnapshotVerbose';
import { catalogEmptyFromPayload } from './catalogSnapshotFlags';
import {
  applyPromotionsSnapshotPayload,
  type RemotePromotionRow,
  type RemotePromotionTargetRow,
} from './promotionsSnapshotSync';
import {
  applyPriceListsSnapshotPayload,
  type RemotePriceListItemRow,
  type RemotePriceListRow,
} from './priceListsSnapshotSync';
import { upsertProductVariantsBatch, type RemoteProductVariantRow } from './productVariantsSnapshotSync';
import { parseIsoString } from './sqlHelpers';
import { markMastersSyncedOk } from '../../sync/core/masterSyncPolicy';
import type {
  RemoteBrandRow,
  RemoteCategoryRow,
  RemoteProductRow,
  RemoteSupplierRow,
} from '../domain/catalogSnapshotTypes';

// ─── Runtime state (cooldown + coalescing) ──────────────────────────────────

const CATALOG_SNAPSHOT_COOLDOWN_MS = 15 * 60 * 1000;
const CATALOG_LAST_PULL_AT_KEY = 'sync.catalog_last_pull_at';

export type CatalogSnapshotSyncResult = {
  applied: boolean;
  /** Probe RPC completed with has_changes:false (0 SQLite writes). */
  probed?: boolean;
  /** Present when the complete remote snapshot reports no sellable (non-misc) products. */
  catalogEmpty?: boolean;
};

const SYNC_NOT_APPLIED: CatalogSnapshotSyncResult = { applied: false };

let activeCatalogSnapshotSync: Promise<CatalogSnapshotSyncResult> | null = null;
let activeCatalogSnapshotSyncForced = false;
let lastSuccessfulCatalogSnapshotAt = 0;

export function resetCatalogSnapshotSyncRuntimeState(): void {
  lastSuccessfulCatalogSnapshotAt = 0;
  activeCatalogSnapshotSync = null;
  activeCatalogSnapshotSyncForced = false;
}

export function getActiveCatalogSnapshotSync(): Promise<CatalogSnapshotSyncResult> | null {
  return activeCatalogSnapshotSync;
}

export function shouldSkipCatalogSnapshotSync(hasSession: () => boolean): boolean {
  return !hasSession();
}

/** Pure: `forceFull` ⇒ null since (full RPC); else cursor incremental. */
export function resolveCatalogSnapshotUpdatedSince(args: {
  forceFull: boolean;
  lastPullAt: string | null;
}): string | null {
  if (args.forceFull) return null;
  return args.lastPullAt;
}

/**
 * Cursor a persistir tras un pull. Nunca `Date.now()`: el reloj del cliente
 * queda adelante de un write concurrente y el próximo incremental no lo ve.
 */
export function resolveCatalogSnapshotCursorToPersist(args: {
  serverCursor: unknown;
  lastPullAt: string | null;
  rowUpdatedAt: Array<string | null | undefined>;
}): string | null {
  const fromServer = parseIsoString(args.serverCursor);
  if (fromServer) return fromServer;
  let max = args.lastPullAt;
  for (const raw of args.rowUpdatedAt) {
    const iso = parseIsoString(raw);
    if (!iso) continue;
    if (!max || iso > max) max = iso;
  }
  return max;
}

function getCoalescedCatalogSnapshotSync(opts?: {
  force?: boolean;
  forceFull?: boolean;
}): Promise<CatalogSnapshotSyncResult> | null {
  // forceFull no se une a un pull incremental en vuelo (puede ser snapshot completo).
  if (opts?.forceFull && !activeCatalogSnapshotSyncForced) return null;
  return activeCatalogSnapshotSync;
}

function isCatalogSnapshotSyncOnCooldown(opts?: {
  force?: boolean;
  forceFull?: boolean;
}): boolean {
  if (opts?.force || opts?.forceFull) return false;
  return Date.now() - lastSuccessfulCatalogSnapshotAt < CATALOG_SNAPSHOT_COOLDOWN_MS;
}

export function assignActiveCatalogSnapshotSync(
  run: Promise<CatalogSnapshotSyncResult>,
  forceFull?: boolean
): Promise<CatalogSnapshotSyncResult> {
  activeCatalogSnapshotSyncForced = Boolean(forceFull);
  const tracked = run.finally(() => {
    if (activeCatalogSnapshotSync === tracked) {
      activeCatalogSnapshotSync = null;
      activeCatalogSnapshotSyncForced = false;
    }
  });
  activeCatalogSnapshotSync = tracked;
  return tracked;
}

function recordCatalogSnapshotSyncSuccess(): void {
  lastSuccessfulCatalogSnapshotAt = Date.now();
}

// ─── Web cache adapter ──────────────────────────────────────────────────────

function applyWebCatalogCacheFromPull(args: {
  isSnapshotComplete: boolean;
  products: RemoteProductRow[];
  stocks: RemoteSnapshotStockRow[];
  stockTransfers: RemoteSnapshotStockTransferRow[];
  stockLots?: RemoteSnapshotStockLotRow[];
  brandRows: RemoteBrandRow[];
  supplierRows: RemoteSupplierRow[];
  categoryRows: RemoteCategoryRow[];
  promotions?: RemotePromotionRow[];
  promotionTargets?: RemotePromotionTargetRow[];
  productVariants?: RemoteProductVariantRow[];
  priceLists?: RemotePriceListRow[];
  priceListItems?: RemotePriceListItemRow[];
}): void {
  const payload = {
    products: args.products,
    stocks: args.stocks,
    stock_transfers: args.stockTransfers,
    stock_lots: args.stockLots ?? [],
    brands: args.brandRows,
    suppliers: args.supplierRows,
    categories: args.categoryRows,
    promotions: args.promotions ?? [],
    promotion_targets: args.promotionTargets ?? [],
    product_variants: args.productVariants ?? [],
    price_lists: args.priceLists ?? [],
    price_list_items: args.priceListItems ?? [],
  };
  if (args.isSnapshotComplete || !getCatalogRamSnapshotCache()) {
    setCatalogRamSnapshotCache(payload);
    return;
  }
  mergeCatalogRamSnapshotCache(payload);
}

// ─── Reconcile diff (DELETE rows absent from snapshot) ──────────────────────

async function loadPendingSyncEntityIds(_conn: SqliteAsyncConnection, _entity: string): Promise<string[]> {
  // ADR-017 Fase 4: cola eliminada — no hay sync_queue
  return [];
}

export async function reconcileEntityByOrg(
  _conn: SqliteAsyncConnection,
  _tableName: string,
  _rows: { id: string; organization_id?: string | null }[],
  _options?: { pendingSyncEntity?: string }
): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — dead code bajo stub no-op.
}

async function reconcileCatalogSnapshotMasters(
  conn: SqliteAsyncConnection,
  args: {
    suppliers: RemoteSupplierRow[];
    brands: RemoteBrandRow[];
    products: RemoteProductRow[];
    categories: RemoteCategoryRow[];
    isSnapshotComplete: boolean;
  }
): Promise<void> {
  const { suppliers, brands, products, categories, isSnapshotComplete } = args;
  if (!isSnapshotComplete) {
    return;
  }

  await reconcileEntityByOrg(conn, 'brands', brands, { pendingSyncEntity: 'brand' });
  await reconcileEntityByOrg(conn, 'suppliers', suppliers, { pendingSyncEntity: 'supplier' });
  await reconcileEntityByOrg(conn, 'products', products);
  await reconcileEntityByOrg(conn, 'categories', categories, { pendingSyncEntity: 'category' });
}

// ─── Batch upserts ──────────────────────────────────────────────────────────

async function upsertProductsBatch(
  _conn: SqliteAsyncConnection,
  products: RemoteProductRow[]
): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — dead code bajo stub no-op.
  if (products.length === 0) {
    catalogSnapshotVerboseLog('[catalogSnapshot] No hay productos para insertar');
    return;
  }
  catalogSnapshotVerboseLog(`[catalogSnapshot] ${products.length} productos (SQLite apply no-op)`);
}

async function upsertBrandsBatch(
  _conn: SqliteAsyncConnection,
  brands: RemoteBrandRow[]
): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — dead code bajo stub no-op.
  const rows = brands.filter((b) => b?.id && b.organization_id);
  if (rows.length === 0) {
    catalogSnapshotVerboseLog('[catalogSnapshot] No hay marcas para insertar');
    return;
  }
  catalogSnapshotVerboseLog(`[catalogSnapshot] ${rows.length} marcas (SQLite apply no-op)`);
}

async function upsertSuppliersBatch(
  _conn: SqliteAsyncConnection,
  suppliers: RemoteSupplierRow[]
): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — dead code bajo stub no-op.
  const rows = suppliers.filter((s) => s?.id && s.organization_id);
  if (rows.length === 0) {
    catalogSnapshotVerboseLog('[catalogSnapshot] No hay proveedores para insertar');
    return;
  }
  catalogSnapshotVerboseLog(`[catalogSnapshot] ${rows.length} proveedores (SQLite apply no-op)`);
}

async function upsertCategoriesBatch(
  _conn: SqliteAsyncConnection,
  categories: RemoteCategoryRow[]
): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — dead code bajo stub no-op.
  const rows = categories.filter((c) => c?.id && c.organization_id);
  if (rows.length === 0) {
    catalogSnapshotVerboseLog('[catalogSnapshot] No hay categorías para insertar');
    return;
  }
  catalogSnapshotVerboseLog(`[catalogSnapshot] ${rows.length} categorías (SQLite apply no-op)`);
}

async function applyCatalogSnapshotMasterRows(
  conn: SqliteAsyncConnection,
  args: {
    suppliers: RemoteSupplierRow[];
    brands: RemoteBrandRow[];
    products: RemoteProductRow[];
    categories: RemoteCategoryRow[];
  }
): Promise<void> {
  await upsertBrandsBatch(conn, args.brands);
  await upsertSuppliersBatch(conn, args.suppliers);
  await upsertCategoriesBatch(conn, args.categories);
  await upsertProductsBatch(conn, args.products);
}

// ─── Stock writes ───────────────────────────────────────────────────────────

type CatalogSnapshotStockPayload = {
  stocks: RemoteSnapshotStockRow[];
  stockTransfers: RemoteSnapshotStockTransferRow[];
  stockLots: RemoteSnapshotStockLotRow[];
};

async function applyCatalogSnapshotStockWrites(
  conn: SqliteAsyncConnection,
  payload: CatalogSnapshotStockPayload
): Promise<void> {
  await applyRemoteCatalogSnapshotStockWrites(conn, {
    stocks: payload.stocks,
    stockTransfers: payload.stockTransfers,
    stockLots: payload.stockLots,
  });
}

async function applyCatalogSnapshotStockWritesInTransaction(
  payload: CatalogSnapshotStockPayload
): Promise<void> {
  catalogSnapshotVerboseLog('[catalogSnapshot] applyCatalogSnapshotPayload completado; iniciando stock snapshot...');
  await applyCatalogSnapshotStockWrites(undefined as unknown as SqliteAsyncConnection, payload);
}

// ─── Apply payload (orchestration) ──────────────────────────────────────────

const isFinalizingStatementError = (e: unknown): boolean => {
  const msg = e instanceof Error ? e.message : String(e);
  return msg.includes('Error finalizing statement') || msg.includes('finalizing statement');
};

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

type CatalogSnapshotApplyPayload = {
  suppliers: RemoteSupplierRow[];
  brands: RemoteBrandRow[];
  products: RemoteProductRow[];
  stocks: RemoteSnapshotStockRow[];
  stockTransfers: RemoteSnapshotStockTransferRow[];
  stockLots: RemoteSnapshotStockLotRow[];
  skuCounters: { organization_id: string; next_sequence: number; updated_at: string }[];
  categories: RemoteCategoryRow[];
  promotions: RemotePromotionRow[];
  promotionTargets: RemotePromotionTargetRow[];
  priceLists: RemotePriceListRow[];
  priceListItems: RemotePriceListItemRow[];
  productVariants: RemoteProductVariantRow[];
  isSnapshotComplete: boolean;
};

async function applyCatalogSnapshotPayload(
  _conn: SqliteAsyncConnection,
  _payload: CatalogSnapshotApplyPayload
): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — dead code bajo stub no-op.
}

async function applyCatalogSnapshotWithRetry(payload: CatalogSnapshotApplyPayload): Promise<void> {
  const stockPayload: CatalogSnapshotStockPayload = {
    stocks: payload.stocks,
    stockTransfers: payload.stockTransfers,
    stockLots: payload.stockLots,
  };

  catalogSnapshotVerboseLog('[catalogSnapshot] Iniciando applyCatalogSnapshotPayload...');
  try {
    await applyCatalogSnapshotPayload(undefined as unknown as SqliteAsyncConnection, payload);
    await applyCatalogSnapshotStockWritesInTransaction(stockPayload);
    catalogSnapshotVerboseLog('[catalogSnapshot] applyCatalogSnapshotPayload completado exitosamente');
  } catch (e) {
    if (isFinalizingStatementError(e)) {
      catalogSnapshotVerboseWarn('[catalogSnapshot] Error finalizing statement en primer intento; reintentando tras 500ms...');
      await delay(500);
      try {
        await applyCatalogSnapshotPayload(undefined as unknown as SqliteAsyncConnection, payload);
        await applyCatalogSnapshotStockWritesInTransaction(stockPayload);
        catalogSnapshotVerboseLog('[catalogSnapshot] applyCatalogSnapshotPayload completado exitosamente tras reintento');
      } catch (retryErr) {
        catalogSnapshotVerboseWarn('[catalogSnapshot] Reintento también falló:', retryErr);
        throw retryErr;
      }
    } else {
      throw e;
    }
  }
}

// ─── RPC fetch + apply ──────────────────────────────────────────────────────

function isCatalogSnapshotPermissionDenied(message: string): boolean {
  return message.toLowerCase().includes('permission denied for function get_mobile_catalog_snapshot');
}

async function fetchCatalogSnapshotRpc(pSince: string | null) {
  let result = await supabase.rpc('get_mobile_catalog_snapshot', {
    p_updated_since: pSince,
  });
  if (result.error && isCatalogSnapshotPermissionDenied(result.error.message)) {
    await supabase.auth.refreshSession();
    result = await supabase.rpc('get_mobile_catalog_snapshot', {
      p_updated_since: pSince,
    });
  }
  return result;
}

async function syncCatalogSnapshotFromRemoteBody(
  forceFull?: boolean
): Promise<CatalogSnapshotSyncResult> {
  const sqliteReady: boolean = false;

  const webCache = getCatalogRamSnapshotCache();
  const webHasCache = webCache != null && webCache.products.length > 0;
  const lastPullAt =
    !forceFull && (sqliteReady || webHasCache) ? await getCatalogLastPullAt() : null;
  const pSince = resolveCatalogSnapshotUpdatedSince({
    forceFull: Boolean(forceFull),
    lastPullAt,
  });
  recordStartupNetworkCall(
    'catalog_snapshot',
    pSince ? 'get_mobile_catalog_snapshot?incremental' : 'get_mobile_catalog_snapshot'
  );
  const { data, error } = await fetchCatalogSnapshotRpc(pSince);
  if (error) {
    captureOpsFailure({ module: 'catalog', op: 'catalog.snapshot_rpc', error: new Error(error.message), ingestError: error.message });
    if (forceFull) {
      throw new SyncPullFailedError('catalog', `No se pudo actualizar catálogo e inventario: ${error.message}`);
    }
    return SYNC_NOT_APPLIED;
  }
  if (!data || typeof data !== 'object') {
    captureOpsFailure({ module: 'catalog', op: 'catalog.snapshot_rpc', error: new Error('No data received from Supabase') });
    if (forceFull) {
      throw new SyncPullFailedError(
        'catalog',
        'No se recibieron datos del servidor para catálogo e inventario.'
      );
    }
    return SYNC_NOT_APPLIED;
  }

  const payload = data as {
    suppliers?: RemoteSupplierRow[];
    brands?: RemoteBrandRow[];
    products?: RemoteProductRow[];
    stocks?: RemoteSnapshotStockRow[];
    stock_transfers?: RemoteSnapshotStockTransferRow[];
    stock_lots?: RemoteSnapshotStockLotRow[];
    sku_counters?: { organization_id: string; next_sequence: number; updated_at: string }[];
    categories?: RemoteCategoryRow[];
    promotions?: RemotePromotionRow[];
    promotion_targets?: RemotePromotionTargetRow[];
    product_variants?: RemoteProductVariantRow[];
    // #767: tipar explicitamente price_lists y price_list_items
    price_lists?: RemotePriceListRow[];
    price_list_items?: RemotePriceListItemRow[];
    snapshot_complete?: boolean;
    has_changes?: boolean;
    catalog_empty?: boolean;
    schema_version?: number;
    server_cursor?: string;
    // Campos futuros del RPC: ignorar (forward-compatible).
    [key: string]: unknown;
  };

  // Contrato versionado: remoto viejo → defaults; remoto nuevo → ignorar keys desconocidas.
  // Campos ausentes en filas ya se toleran en upserts (COALESCE / null).
  const remoteSchemaVersion = Number(payload.schema_version ?? 0);
  if (
    Number.isFinite(remoteSchemaVersion) &&
    remoteSchemaVersion > 0 &&
    remoteSchemaVersion < MIN_SUPPORTED_CATALOG_SNAPSHOT_SCHEMA_VERSION
  ) {
    catalogSnapshotVerboseWarn(
      `[catalogSnapshot] schema_version remoto ${remoteSchemaVersion} < mínimo ${MIN_SUPPORTED_CATALOG_SNAPSHOT_SCHEMA_VERSION}; aplicando con defaults`
    );
  }

  if (payload.has_changes === false) {
    catalogSnapshotVerboseLog('[catalogSnapshot] Sin cambios remotos (has_changes: false)');
    const webCacheEmpty =
      !getCatalogRamSnapshotCache() || getCatalogRamSnapshotCache()!.products.length === 0;
    let nativeCatalogEmpty = false;
    if (!forceFull && (webCacheEmpty || nativeCatalogEmpty)) {
      catalogSnapshotVerboseLog(
        '[catalogSnapshot] Cache local vacío; forzando snapshot completo tras incremental vacío'
      );
      return syncCatalogSnapshotFromRemoteBody(true);
    }
    if (payload.server_cursor) {
      await persistAppSettingSafe(CATALOG_LAST_PULL_AT_KEY, payload.server_cursor);
      recordSyncPullTimestamp(CATALOG_LAST_PULL_AT_KEY, payload.server_cursor);
    }
    markCatalogHydrated();
    markMastersSyncedOk();
    return { applied: false, probed: true };
  }

  const isSnapshotComplete = payload.snapshot_complete === true;
  const catalogEmpty = catalogEmptyFromPayload(payload);
  const appliedResult: CatalogSnapshotSyncResult = {
    applied: true,
    ...(catalogEmpty !== undefined ? { catalogEmpty } : {}),
  };
  const supplierRows = Array.isArray(payload.suppliers) ? payload.suppliers : [];
  const brandRows = Array.isArray(payload.brands) ? payload.brands : [];
  const products = Array.isArray(payload.products) ? payload.products : [];
  const stocks = mergeRemoteSnapshotStocks(Array.isArray(payload.stocks) ? payload.stocks : []);
  const stockTransfers = Array.isArray(payload.stock_transfers) ? payload.stock_transfers : [];
  const stockLots = Array.isArray(payload.stock_lots) ? payload.stock_lots : [];
  const skuCounters = Array.isArray(payload.sku_counters) ? payload.sku_counters : [];
  const categoryRows = Array.isArray(payload.categories) ? payload.categories : [];
  const promotionRows = Array.isArray(payload.promotions) ? payload.promotions : [];
  const promotionTargetRows = Array.isArray(payload.promotion_targets) ? payload.promotion_targets : [];
  const priceListRows = Array.isArray(payload.price_lists) ? payload.price_lists : [];
  const priceListItemRows = Array.isArray(payload.price_list_items) ? payload.price_list_items : [];
  const productVariantRows = Array.isArray(payload.product_variants) ? payload.product_variants : [];

  catalogSnapshotVerboseLog(
    `[catalogSnapshot] Payload recibido: ${products.length} products, ${brandRows.length} brands, ${supplierRows.length} suppliers, ${categoryRows.length} categories, ${promotionRows.length} promotions, ${promotionTargetRows.length} promotion_targets, ${productVariantRows.length} product_variants, ${stocks.length} stocks, ${stockTransfers.length} transfers, ${stockLots.length} lots, ${skuCounters.length} skuCounters`
  );

  if (!sqliteReady) {
    applyWebCatalogCacheFromPull({
      isSnapshotComplete,
      products,
      stocks,
      stockTransfers,
      stockLots,
      brandRows,
      supplierRows,
      categoryRows,
      promotions: promotionRows,
      promotionTargets: promotionTargetRows,
      productVariants: productVariantRows,
      priceLists: priceListRows,
      priceListItems: priceListItemRows,
    });
    catalogSnapshotVerboseLog(
      '[catalogSnapshot] Snapshot remoto OK; apply SQLite omitido en web (UI lee de Supabase)'
    );
    // applyWebCatalogCacheFromPull → set/merge cache ya publica catalogSynced
    const cursorIso = resolveCatalogSnapshotCursorToPersist({
      serverCursor: payload.server_cursor,
      lastPullAt,
      rowUpdatedAt: [
        ...products.map((row) => row.updated_at),
        ...brandRows.map((row) => row.updated_at),
        ...supplierRows.map((row) => row.updated_at),
        ...categoryRows.map((row) => row.updated_at),
        ...promotionRows.map((row) => row.updated_at),
      ],
    });
    if (cursorIso) {
      await persistAppSettingSafe(CATALOG_LAST_PULL_AT_KEY, cursorIso);
      recordSyncPullTimestamp(CATALOG_LAST_PULL_AT_KEY, cursorIso);
    }
    markCatalogHydrated();
    markMastersSyncedOk();
    return appliedResult;
  }

  try {
    await applyCatalogSnapshotWithRetry({
      suppliers: supplierRows,
      brands: brandRows,
      products,
      stocks,
      stockTransfers,
      stockLots,
      skuCounters,
      categories: categoryRows,
      promotions: promotionRows,
      promotionTargets: promotionTargetRows,
      priceLists: priceListRows,
      priceListItems: priceListItemRows,
      productVariants: productVariantRows,
      isSnapshotComplete,
    });
    await dedupeDuplicateStocksRows();
    catalogSnapshotVerboseLog('[catalogSnapshot] Snapshot aplicado y stocks deduplicados correctamente');
    const nativeCursorIso = resolveCatalogSnapshotCursorToPersist({
      serverCursor: payload.server_cursor,
      lastPullAt,
      rowUpdatedAt: [
        ...products.map((row) => row.updated_at),
        ...brandRows.map((row) => row.updated_at),
        ...supplierRows.map((row) => row.updated_at),
        ...categoryRows.map((row) => row.updated_at),
        ...promotionRows.map((row) => row.updated_at),
      ],
    });
    if (nativeCursorIso) {
      await persistAppSettingSafe(CATALOG_LAST_PULL_AT_KEY, nativeCursorIso);
      recordSyncPullTimestamp(CATALOG_LAST_PULL_AT_KEY, nativeCursorIso);
    }
    markMastersSyncedOk();

    applyWebCatalogCacheFromPull({
              isSnapshotComplete,
              products,
              stocks,
              stockTransfers,
              brandRows,
              supplierRows,
              categoryRows,
              promotions: promotionRows,
              promotionTargets: promotionTargetRows,
              productVariants: productVariantRows,
              priceLists: priceListRows,
              priceListItems: priceListItemRows,
            });
  } catch (e) {
    applyWebCatalogCacheFromPull({
              isSnapshotComplete,
              products,
              stocks,
              stockTransfers,
              brandRows,
              supplierRows,
              categoryRows,
              promotions: promotionRows,
              promotionTargets: promotionTargetRows,
              productVariants: productVariantRows,
              priceLists: priceListRows,
              priceListItems: priceListItemRows,
            });
            catalogSnapshotVerboseLog(
              '[catalogSnapshot] Snapshot remoto OK; apply SQLite omitido en web (UI lee de Supabase)'
            );
            // applyWebCatalogCacheFromPull ya publicó catalogSynced
            return appliedResult;
  }
  return appliedResult;
}

// ─── Public entry point ─────────────────────────────────────────────────────

function catalogSnapshotHasSessionFromAuthStore(): boolean {
  const { session } = useAuthStore.getState();
  return !!session;
}

/**
 * Hidrata SQLite con productos, stocks y transferencias desde Supabase (RPC `get_mobile_catalog_snapshot`).
 * Cubre empleados y dispositivos donde `ensureLocalDataMatchesAuthUser` dejó el catálogo vacío.
 * Las transferencias permiten que otro dispositivo en la sucursal destino vea envíos `sent` y reciba/rechace.
 *
 * - `force`: saltea cooldown; sigue usando `p_updated_since` si hay cursor (incremental).
 * - `forceFull`: saltea cooldown y pide snapshot completo (`p_updated_since = null`).
 *   Solo manual / vacío / primera sync — no mount/focus/reconnect.
 *
 * `opts.hasSession` permite inyectar el chequeo de sesión (tests). Por defecto usa auth store local.
 */
export async function syncCatalogSnapshotFromRemote(opts?: {
  hasSession?: () => boolean;
  force?: boolean;
  forceFull?: boolean;
}): Promise<CatalogSnapshotSyncResult> {
  const stack = new Error().stack?.split('\n').slice(2, 5).join(' | ') ?? 'unknown';
  catalogSnapshotVerboseWarn('[catalogSnapshot] syncCatalogSnapshotFromRemote llamado desde:', stack);
  const hasSession = opts?.hasSession ?? catalogSnapshotHasSessionFromAuthStore;
  if (shouldSkipCatalogSnapshotSync(hasSession)) {
    if (opts?.force || opts?.forceFull) {
      throw new SyncPullFailedError('catalog', 'No hay sesión activa.');
    }
    return SYNC_NOT_APPLIED;
  }
  const coalesceOpts = { force: opts?.force, forceFull: opts?.forceFull };
  const activeSync = getCoalescedCatalogSnapshotSync(coalesceOpts);
  if (activeSync) {
    catalogSnapshotVerboseWarn('[catalogSnapshot] coalescando con sync activa');
    return activeSync;
  }
  if (isCatalogSnapshotSyncOnCooldown(coalesceOpts)) {
    catalogSnapshotVerboseWarn('[catalogSnapshot] saltando por cooldown');
    return SYNC_NOT_APPLIED;
  }

  const forceFull = opts?.forceFull === true;
  const run = withSyncMutex(async () => {
    const result = await syncCatalogSnapshotFromRemoteBody(forceFull);
    // Solo avance de cooldown si el RPC terminó bien (apply o probe has_changes:false).
    if (result.applied || result.probed) {
      recordCatalogSnapshotSyncSuccess();
      markCatalogHydrated();
    }
    return result;
  });
  return assignActiveCatalogSnapshotSync(run, forceFull);
}

/**
 * Tras un write local: espera cualquier sync en vuelo (puede ser pre-write) y pide forceFull.
 * Evita coalescer el refresh post-write con un pull que empezó antes del INSERT/UPDATE.
 */
export async function syncCatalogSnapshotAfterLocalWrite(): Promise<CatalogSnapshotSyncResult> {
  if (activeCatalogSnapshotSync) {
    await activeCatalogSnapshotSync.catch(() => undefined);
  }
  return syncCatalogSnapshotFromRemote({ force: true, forceFull: true });
}
